import { listOfMonths } from "../constants.ts";
import { getMonthlyTotalAmount } from "./budget.ts";
import {
  BudgetData,
  BudgetDataItem,
  CashAccounts,
  CashFlowMonth,
  EssentialOverrides,
  EssentialsCoverage,
  IncomeEvent,
  IncomeMonth,
  IncomeStats,
  OwnerPayConfig,
  RecurringFrequency,
  RecurringIncome,
  Runway,
} from "../types.ts";

/**
 * Pure cash-flow engine. No React, no atoms, no network — everything here is a
 * deterministic function of its arguments so it can be unit tested and reused
 * from the dashboard, onboarding and the goal projector.
 *
 * Money is in the same USD base convention used by the existing budget items;
 * currency conversion for display stays in helper.ts.
 */

// Slice taken off the trailing average before it becomes owner's pay, so a run
// of good months doesn't set an unsustainable salary.
const SAFETY_FACTOR = 0.15;

// Cash kept in checking before surplus is swept into the buffer.
const OPERATING_MINIMUM = 500;

// Runway is capped for display / JSON friendliness (no Infinity).
const MAX_RUNWAY_MONTHS = 999;

// Hard cap on generated paydays per recurring rule, so a stray anchor date far
// in the future can't blow up the projection.
const MAX_RECURRING_OCCURRENCES = 500;

export const DEFAULT_OWNER_PAY_CONFIG: OwnerPayConfig = {
  mode: "irregular",
  method: "trailing_median",
  trailingMonths: 6,
  monthlyDraw: 0,
  bufferTargetMonths: 3,
  taxReservePercent: 25,
  projectionHorizonMonths: 6,
};

export const defaultCashAccounts = (now: Date = new Date()): CashAccounts => ({
  checkingBalance: 0,
  bufferBalance: 0,
  taxBalance: 0,
  asOf: isoDate(now),
});

// --- small helpers -------------------------------------------------------------

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100 || 0;

const clamp01 = (n: number) => Math.max(0, Math.min(1, n || 0));

const isoDate = (d: Date) => d.toISOString().slice(0, 10);

interface YearMonth {
  month: string;
  year: number;
}

export const ymFromISO = (iso: string): YearMonth => {
  const [year, month] = String(iso)
    .split("-")
    .map((part) => Number(part));
  return {
    month: listOfMonths[(month || 1) - 1] || listOfMonths[0],
    year: year || new Date().getFullYear(),
  };
};

export const ymFromDate = (d: Date): YearMonth => ({
  month: listOfMonths[d.getMonth()],
  year: d.getFullYear(),
});

export const addMonths = ({ month, year }: YearMonth, n: number): YearMonth => {
  const total = year * 12 + listOfMonths.indexOf(month) + n;
  return {
    month: listOfMonths[((total % 12) + 12) % 12],
    year: Math.floor(total / 12),
  };
};

const sameYM = (a: YearMonth, b: YearMonth) =>
  a.month === b.month && a.year === b.year;

const percentile = (sorted: number[], p: number): number => {
  if (!sorted.length) return 0;
  if (sorted.length === 1) return sorted[0];
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (rank - lo) * (sorted[hi] - sorted[lo]);
};

const parseUTC = (iso: string) => new Date(`${iso}T00:00:00Z`);

const daysInMonth = (year: number, monthIndex: number) =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

// --- recurring income ------------------------------------------------------

/**
 * A single occurrence of a recurring rule expressed as a monthly figure — used
 * when seeding onboarding budget rows from paycheck rules.
 */
export const monthlyEquivalent = (
  amount: number,
  frequency: RecurringFrequency,
): number => {
  switch (frequency) {
    case "Weekly":
      return round2((amount * 52) / 12);
    case "Every 2 weeks":
      return round2((amount * 26) / 12);
    case "Semi-Monthly":
      return round2(amount * 2);
    case "Monthly":
    default:
      return round2(amount);
  }
};

/**
 * Turn recurring paycheck rules into concrete dated income events covering
 * `count` months from `start`. Interval rules (weekly / every 2 weeks) step from
 * the anchor date; calendar rules (semi-monthly / monthly) land on the anchor
 * day-of-month (and anchor + 15 for semi-monthly), clamped to month length.
 * Generated events are always `received` with full confidence.
 */
export const expandRecurringIncome = (
  rules: RecurringIncome[],
  start: YearMonth,
  count: number,
): IncomeEvent[] => {
  if (!rules.length || count <= 0) {
    return [];
  }

  const startIndex = listOfMonths.indexOf(start.month);
  const windowStart = new Date(Date.UTC(start.year, startIndex, 1));
  const endYM = addMonths(start, count - 1);
  const endIndex = listOfMonths.indexOf(endYM.month);
  const windowEnd = new Date(
    Date.UTC(endYM.year, endIndex, daysInMonth(endYM.year, endIndex)),
  );

  const events: IncomeEvent[] = [];

  rules.forEach((rule) => {
    if (!rule.active || !rule.amount || !rule.anchorDate) {
      return;
    }

    const anchor = parseUTC(rule.anchorDate);
    if (Number.isNaN(anchor.getTime())) {
      return;
    }

    const dates: string[] = [];

    if (rule.frequency === "Weekly" || rule.frequency === "Every 2 weeks") {
      const step = rule.frequency === "Weekly" ? 7 : 14;
      const cursor = new Date(anchor);
      let guard = 0;

      while (cursor > windowStart && guard < MAX_RECURRING_OCCURRENCES) {
        cursor.setUTCDate(cursor.getUTCDate() - step);
        guard += 1;
      }
      guard = 0;
      while (cursor < windowStart && guard < MAX_RECURRING_OCCURRENCES) {
        cursor.setUTCDate(cursor.getUTCDate() + step);
        guard += 1;
      }
      guard = 0;
      while (cursor <= windowEnd && guard < MAX_RECURRING_OCCURRENCES) {
        dates.push(cursor.toISOString().slice(0, 10));
        cursor.setUTCDate(cursor.getUTCDate() + step);
        guard += 1;
      }
    } else {
      const anchorDay = anchor.getUTCDate();

      for (let i = 0; i < count; i += 1) {
        const ym = addMonths(start, i);
        const monthIndex = listOfMonths.indexOf(ym.month);
        const dim = daysInMonth(ym.year, monthIndex);
        const push = (day: number) =>
          dates.push(
            new Date(Date.UTC(ym.year, monthIndex, Math.min(day, dim)))
              .toISOString()
              .slice(0, 10),
          );

        push(anchorDay);
        if (rule.frequency === "Semi-Monthly") {
          push(anchorDay + 15);
        }
      }
    }

    dates.forEach((date) =>
      events.push({
        id: `${rule.id}:${date}`,
        date,
        amount: rule.amount,
        label: rule.label,
        status: "received",
        confidence: 1,
        // A recurring paycheck is treated as already-taxed, so it doesn't feed
        // the tax reserve again (matters for "mixed" mode).
        taxable: false,
      }),
    );
  });

  return events;
};

// --- income series & stats ---------------------------------------------------

/**
 * Bucket income events into `count` consecutive months beginning at `start`.
 * Received payments count at face value; expected payments are weighted by
 * their confidence.
 */
export const getMonthlyIncomeSeries = (
  events: IncomeEvent[],
  start: YearMonth,
  count: number,
): IncomeMonth[] => {
  const months: IncomeMonth[] = [];

  for (let i = 0; i < Math.max(0, count); i++) {
    months.push({ ...addMonths(start, i), received: 0, expectedWeighted: 0 });
  }

  events.forEach((event) => {
    const bucket = months.find((m) => sameYM(m, ymFromISO(event.date)));

    if (!bucket) {
      return;
    }

    if (event.status === "received") {
      bucket.received = round2(bucket.received + event.amount);
    } else {
      bucket.expectedWeighted = round2(
        bucket.expectedWeighted + event.amount * clamp01(event.confidence),
      );
    }
  });

  return months;
};

const summarize = (values: number[]): IncomeStats => {
  if (!values.length) {
    return { average: 0, median: 0, min: 0, low: 0, months: 0 };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const sum = sorted.reduce((acc, val) => acc + val, 0);

  return {
    average: round2(sum / sorted.length),
    median: round2(percentile(sorted, 50)),
    min: round2(sorted[0]),
    low: round2(percentile(sorted, 20)),
    months: values.filter((v) => v > 0).length,
  };
};

/**
 * Income stats over the completed months immediately before `now` (the current,
 * partial month is excluded). Zero months are kept in the sample on purpose — a
 * dry spell should pull the numbers down.
 */
export const getTrailingIncomeStats = (
  events: IncomeEvent[],
  config: OwnerPayConfig,
  now: Date = new Date(),
  recurring: RecurringIncome[] = [],
): IncomeStats => {
  const window = Math.max(1, config.trailingMonths);
  const windowStart = addMonths(ymFromDate(now), -window);
  const all = [
    ...events,
    ...expandRecurringIncome(recurring, windowStart, window),
  ];
  const series = getMonthlyIncomeSeries(all, windowStart, window);

  return summarize(series.map((m) => round2(m.received + m.expectedWeighted)));
};

// --- owner's pay ------------------------------------------------------------

/**
 * The salary the owner can safely pay themselves each month, after holding back
 * the tax reserve. Method controls how conservative the base figure is.
 */
export const getRecommendedDraw = (
  stats: IncomeStats,
  config: OwnerPayConfig,
): number => {
  let base = 0;

  switch (config.method) {
    case "trailing_average":
      base = stats.average * (1 - SAFETY_FACTOR);
      break;
    case "lean_month":
      base = stats.low;
      break;
    case "trailing_median":
    default:
      base = stats.median;
      break;
  }

  const afterTax = base * (1 - clamp01(config.taxReservePercent / 100));

  return Math.max(0, round2(afterTax));
};

/**
 * For a regular paycheck: what's actually left over this calendar month —
 * cash on hand plus this month's income, minus this month's budgeted
 * expenses. Unlike `getRecommendedDraw` (a trailing-income estimate meant for
 * irregular income), a regular paycheck plus a real budget means the safe
 * number is just what's left.
 */
export const getRemainingCash = (
  budget: BudgetData[],
  events: IncomeEvent[],
  accounts: CashAccounts,
  now: Date = new Date(),
  recurring: RecurringIncome[] = [],
): number => {
  const start = ymFromDate(now);
  const all = [...events, ...expandRecurringIncome(recurring, start, 1)];
  const [thisMonth] = getMonthlyIncomeSeries(all, start, 1);
  const monthlyIncome = round2(thisMonth.received + thisMonth.expectedWeighted);
  const monthlyExpense = getMonthlyTotalAmount(
    budget,
    start.month,
    start.year,
    "expense",
  );

  return round2(
    (Number(accounts.checkingBalance) || 0) + monthlyIncome - monthlyExpense,
  );
};

// --- expenses --------------------------------------------------------------

export const isItemEssential = (
  item: BudgetDataItem,
  overrides: EssentialOverrides = {},
): boolean => {
  const key = item.budget_id != null ? String(item.budget_id) : "";

  if (
    key &&
    overrides &&
    Object.prototype.hasOwnProperty.call(overrides, key)
  ) {
    return overrides[key];
  }

  return item.is_essential ?? true;
};

/**
 * Average monthly essential / discretionary spend across whatever months the
 * budget covers. Recurring frequency is already normalised to a monthly figure
 * on each budget item.
 */
export const getExpenseBurn = (
  budget: BudgetData[],
  overrides: EssentialOverrides = {},
): { essential: number; discretionary: number } => {
  if (!budget.length) {
    return { essential: 0, discretionary: 0 };
  }

  let essential = 0;
  let discretionary = 0;

  budget.forEach((entry) => {
    entry.expense.forEach((item) => {
      if (isItemEssential(item, overrides)) {
        essential += item.value;
      } else {
        discretionary += item.value;
      }
    });
  });

  return {
    essential: round2(essential / budget.length),
    discretionary: round2(discretionary / budget.length),
  };
};

export const getBufferTarget = (
  avgEssentialBurn: number,
  bufferTargetMonths: number,
): number =>
  round2(Math.max(0, avgEssentialBurn) * Math.max(0, bufferTargetMonths));

// --- runway & projection --------------------------------------------------

/**
 * How long spendable cash (checking + buffer, tax reserve excluded) covers
 * essential spend, as of today.
 */
export const computeRunway = (
  accounts: CashAccounts,
  avgEssentialBurn: number,
  now: Date = new Date(),
): Runway => {
  const spendable =
    (Number(accounts.checkingBalance) || 0) +
    (Number(accounts.bufferBalance) || 0);
  const raw =
    avgEssentialBurn > 0 ? spendable / avgEssentialBurn : MAX_RUNWAY_MONTHS;
  // Guard against NaN / Infinity / negative runway so the date math below can't
  // produce an invalid Date.
  const months = Number.isFinite(raw)
    ? Math.max(0, Math.min(raw, MAX_RUNWAY_MONTHS))
    : MAX_RUNWAY_MONTHS;

  const base = Number.isNaN(now.getTime()) ? new Date() : new Date(now);
  const until = new Date(base);
  until.setMonth(until.getMonth() + Math.min(Math.floor(months), 600));

  return { months: round2(months), until: isoDate(until) };
};

/**
 * Month-by-month projection of checking / buffer / tax balances across the
 * configured horizon.
 *
 * Each month: inflow lands, the tax slice is moved to the tax reserve, essential
 * + discretionary spend and the owner's draw leave checking. If checking would
 * fall below the operating minimum the shortfall is pulled from the buffer;
 * surplus above the minimum is swept into the buffer up to its target.
 */
export const projectCashFlow = (
  accounts: CashAccounts,
  events: IncomeEvent[],
  burn: { essential: number; discretionary: number },
  config: OwnerPayConfig,
  now: Date = new Date(),
  recurring: RecurringIncome[] = [],
): CashFlowMonth[] => {
  const horizon = Math.max(0, config.projectionHorizonMonths);
  const start = ymFromDate(now);
  const all = [...events, ...expandRecurringIncome(recurring, start, horizon)];
  const series = getMonthlyIncomeSeries(all, start, horizon);
  // The tax reserve only draws from taxable income — a W-2 paycheck entered as
  // recurring income is already withheld.
  const taxableSeries = getMonthlyIncomeSeries(
    all.filter((event) => event.taxable !== false),
    start,
    horizon,
  );
  const bufferTarget = getBufferTarget(
    burn.essential,
    config.bufferTargetMonths,
  );

  let checking = accounts.checkingBalance;
  let buffer = accounts.bufferBalance;
  let tax = accounts.taxBalance;

  return series.map((month, index) => {
    const inflow = round2(month.received + month.expectedWeighted);
    const taxableInflow = round2(
      taxableSeries[index].received + taxableSeries[index].expectedWeighted,
    );
    const taxReserved = round2(
      taxableInflow * clamp01(config.taxReservePercent / 100),
    );

    tax = round2(tax + taxReserved);
    checking = round2(
      checking +
        inflow -
        taxReserved -
        burn.essential -
        burn.discretionary -
        config.monthlyDraw,
    );

    let bufferContribution = 0;

    if (checking < OPERATING_MINIMUM) {
      const pulled = Math.min(
        OPERATING_MINIMUM - checking,
        Math.max(0, buffer),
      );
      checking = round2(checking + pulled);
      buffer = round2(buffer - pulled);
      bufferContribution = round2(-pulled);
    } else {
      const room = Math.max(0, bufferTarget - buffer);
      const swept = Math.min(checking - OPERATING_MINIMUM, room);
      checking = round2(checking - swept);
      buffer = round2(buffer + swept);
      bufferContribution = round2(swept);
    }

    const runwayMonths =
      burn.essential > 0
        ? round2(
            Math.min((checking + buffer) / burn.essential, MAX_RUNWAY_MONTHS),
          )
        : MAX_RUNWAY_MONTHS;

    return {
      month: month.month,
      year: month.year,
      inflow,
      taxReserved,
      essentialOutflow: burn.essential,
      discretionaryOutflow: burn.discretionary,
      ownerDraw: config.monthlyDraw,
      bufferContribution,
      endingChecking: checking,
      endingBuffer: buffer,
      endingTax: tax,
      runwayMonths,
    };
  });
};

export const essentialsCoverage = (
  income: number,
  avgEssentialBurn: number,
): EssentialsCoverage => {
  if (avgEssentialBurn <= 0) {
    return { ratio: income > 0 ? MAX_RUNWAY_MONTHS : 1, covered: true };
  }

  return {
    ratio: round2(income / avgEssentialBurn),
    covered: income >= avgEssentialBurn,
  };
};

// --- one-shot dashboard build ------------------------------------------------

interface CashFlowSummary {
  stats: IncomeStats;
  burn: { essential: number; discretionary: number };
  recommendedDraw: number;
  bufferTarget: number;
  runway: Runway;
  projection: CashFlowMonth[];
  leanMonthIncome: number;
  leanCoverage: EssentialsCoverage;
  projectedAnnualNet: number;
}

/**
 * Everything the cash-flow dashboard and the goal projector need, derived in one
 * place so components stay declarative.
 */
export const buildCashFlowSummary = (
  budget: BudgetData[],
  events: IncomeEvent[],
  accounts: CashAccounts,
  config: OwnerPayConfig,
  overrides: EssentialOverrides = {},
  now: Date = new Date(),
  recurring: RecurringIncome[] = [],
): CashFlowSummary => {
  const stats = getTrailingIncomeStats(events, config, now, recurring);
  const burn = getExpenseBurn(budget, overrides);
  const runway = computeRunway(accounts, burn.essential, now);
  const projection = projectCashFlow(
    accounts,
    events,
    burn,
    config,
    now,
    recurring,
  );
  const annualProjection = projectCashFlow(
    accounts,
    events,
    burn,
    { ...config, projectionHorizonMonths: 12 },
    now,
    recurring,
  );
  const projectedAnnualNet = round2(
    annualProjection.reduce(
      (acc, month) =>
        acc +
        month.inflow -
        month.taxReserved -
        month.essentialOutflow -
        month.discretionaryOutflow,
      0,
    ),
  );

  const recommendedDraw =
    config.mode === "regular"
      ? getRemainingCash(budget, events, accounts, now, recurring)
      : getRecommendedDraw(stats, config);

  return {
    stats,
    burn,
    recommendedDraw,
    bufferTarget: getBufferTarget(burn.essential, config.bufferTargetMonths),
    runway,
    projection,
    leanMonthIncome: stats.low,
    leanCoverage: essentialsCoverage(stats.low, burn.essential),
    projectedAnnualNet,
  };
};
